/**
 * Returns true when `url` parses via the WHATWG `URL` constructor and uses the
 * `http:` or `https:` protocol.
 *
 * Use whenever the URL will be rendered as a clickable target or passed to
 * `window.location` / `chrome.tabs.update`, to reject `javascript:`, `file:`,
 * `data:` and other non-navigation protocols.
 */
export const isValidHttpUrl = (url: string): boolean => {
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return false;
	}
	return parsed.protocol === 'http:' || parsed.protocol === 'https:';
};

/**
 * Returns the normalised `href` of `url` when it is an http(s) URL, otherwise `undefined`.
 *
 * With `allowSchemeless`, input that has no scheme (e.g. `example.com`) is treated as `https://`.
 * Input with any other scheme (`javascript:`, `data:`, `file:` ...) is always rejected.
 */
export const toSafeHttpUrl = (
	url: string | undefined,
	{ allowSchemeless = false }: { allowSchemeless?: boolean } = {},
): string | undefined => {
	const trimmed = url?.trim();
	if (!trimmed) {
		return undefined;
	}

	let parsed: URL;
	try {
		parsed = new URL(trimmed);
	} catch {
		if (!allowSchemeless) {
			return undefined;
		}
		try {
			parsed = new URL(`https://${trimmed}`);
		} catch {
			return undefined;
		}
	}

	return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : undefined;
};
